import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Search, Layers, FileText } from "lucide-react";

// Wrap the first occurrence of the query in a <mark> so estimators spot the match instantly.
function hl(text, q) {
  const s = text == null ? "" : String(text);
  const needle = (q || "").trim();
  if (!needle) return s;
  const i = s.toLowerCase().indexOf(needle.toLowerCase());
  if (i < 0) return s;
  return (<>{s.slice(0, i)}<mark className="bg-[#F4C400]/60 text-slate-900 rounded px-0.5">{s.slice(i, i + needle.length)}</mark>{s.slice(i + needle.length)}</>);
}

// Scoped search inside a single bid: sheets, sheet numbers, drawing text, documents.
export function InBidSearch({ bidId, onSheet, onDoc }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const run = (v) => {
    setQ(v); setOpen(true);
    if (timer.current) clearTimeout(timer.current);
    if (v.trim().length < 2) { setRes(null); return; }
    timer.current = setTimeout(async () => {
      try { const { data } = await api.get("/search", { params: { q: v, bid_id: bidId } }); setRes(data.results || []); }
      catch { setRes([]); }
    }, 250);
  };

  const pick = (r) => {
    setOpen(false); const term = q; setQ(""); setRes(null);
    if (r.type === "sheet") onSheet?.(r.sheet_id, term);
    else onDoc?.();
  };

  return (
    <div className="relative" ref={ref} data-testid="inbid-search">
      <div className="flex items-center gap-2 h-9 px-3 rounded-md border border-slate-200 focus-within:border-[#003E87] w-64">
        <Search className="w-4 h-4 shrink-0 text-slate-400" />
        <input value={q} onChange={(e) => run(e.target.value)} onFocus={() => q && setOpen(true)}
          placeholder="Search sheets, notes, drawings…" data-testid="inbid-search-input"
          className="flex-1 bg-transparent text-sm outline-none" />
      </div>
      {open && res && (
        <div className="absolute right-0 top-full mt-1 w-96 max-h-96 overflow-auto bg-white rounded-lg shadow-xl border border-slate-200 py-1 z-50" data-testid="inbid-search-results">
          {res.length === 0 ? <div className="px-4 py-3 text-xs text-slate-400">No matches in this bid</div> : res.map((r, i) => (
            <button key={i} onMouseDown={() => pick(r)} data-testid={`inbid-result-${r.sheet_id || r.document_id}`}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-50 last:border-0">
              <div className="flex items-center gap-2 text-sm text-slate-800">
                {r.type === "sheet" ? <Layers className="w-3.5 h-3.5 text-[#003E87] shrink-0" /> : <FileText className="w-3.5 h-3.5 text-[#003E87] shrink-0" />}
                {r.sheet_number ? <span className="font-mono text-xs text-[#003E87] shrink-0">{r.sheet_number}</span> : null}
                <span className="truncate flex-1">{hl(r.name, q)}</span>
              </div>
              {r.snippet && <div className="text-[11px] text-slate-500 pl-5 truncate">{hl(r.snippet, q)}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
